(() => {
  if (window.__COMPONENT_TOAST__) return;
  window.__COMPONENT_TOAST__ = true;

  class ComponentToast extends HTMLElement {
    static targetStates = new WeakMap();

    constructor() {
      super();
      this.hideTimer = null;
      this.removeTimer = null;
      this.restoreTarget = null;
      this.showTransform = "";
      this.toastDuration = 3000;
    }

    connectedCallback() {
      this.open();
    }

    disconnectedCallback() {
      clearTimeout(this.hideTimer);
      clearTimeout(this.removeTimer);
      this.restoreTarget?.();
      this.restoreTarget = null;
    }

    configure(options) {
      const { message, type, theme, position, duration, targetNode } = options;

      this.toastDuration = duration;
      this.restoreTarget = this.prepareTarget(targetNode);
      this.setAttribute("data-type", type);
      this.setAttribute("data-position", position);
      this.classList.toggle("light", theme === "light");
      this.classList.toggle("dark", theme === "dark");
      this.applyPosition(position, targetNode);

      const messageEl = this.querySelector(".component-message__text");
      if (messageEl) messageEl.textContent = message;
    }

    applyPosition(position, targetNode) {
      const { styles, hiddenTransform, showTransform } = this.getPlacement(position, targetNode);

      this.style.position = targetNode ? "absolute" : "fixed";
      this.style.top = styles.top;
      this.style.right = styles.right;
      this.style.bottom = styles.bottom;
      this.style.left = styles.left;
      this.style.transform = hiddenTransform;
      this.showTransform = showTransform;
    }

    open() {
      clearTimeout(this.hideTimer);
      clearTimeout(this.removeTimer);

      requestAnimationFrame(() => {
        this.style.transform = this.showTransform;
        this.classList.add("show");
      });

      if (this.toastDuration > 0) {
        this.hideTimer = setTimeout(() => this.hide(), this.toastDuration);
      }
    }

    hide() {
      clearTimeout(this.hideTimer);
      clearTimeout(this.removeTimer);
      this.classList.remove("show");
      this.removeTimer = setTimeout(() => this.remove(), 250);
    }

    static create(options) {
      if (!options.message) return null;
      const config = {
        message: options.message,
        type: options.type || "info",
        theme: options.theme || "dark",
        position: options.position || "top",
        duration: options.duration ?? 3000,
        targetNode: options.target || null,
      };

      const toast = new ComponentToast();
      toast.className = "component-message";
      toast.setAttribute("role", "alert");
      toast.setAttribute("aria-live", "polite");
      toast.setAttribute("aria-atomic", "true");
      const content = document.querySelector("component-message .component-message__content");
      if (content) toast.innerHTML = content.outerHTML;
      else toast.innerHTML = "<div class='component-message__content'><div class='component-message__text'></div></div>";
      toast.configure(config);

      const root = config.targetNode || document.body;
      root.appendChild(toast);
      return toast;
    }

    prepareTarget(targetNode) {
      if (!targetNode || targetNode === document.body || targetNode === document.documentElement) return null;

      const saved = ComponentToast.targetStates.get(targetNode);
      if (saved) {
        saved.count += 1;
        return () => this.releaseTarget(targetNode);
      }

      const state = {
        count: 1,
        inlinePosition: targetNode.style.position,
        shouldRestorePosition: window.getComputedStyle(targetNode).position === "static",
      };
      if (state.shouldRestorePosition) targetNode.style.position = "relative";
      ComponentToast.targetStates.set(targetNode, state);
      return () => this.releaseTarget(targetNode);
    }

    releaseTarget(targetNode) {
      const state = ComponentToast.targetStates.get(targetNode);
      if (!state) return;

      state.count -= 1;
      if (state.count > 0) return;

      if (state.shouldRestorePosition) targetNode.style.position = state.inlinePosition;
      ComponentToast.targetStates.delete(targetNode);
    }

    getPlacement(position, targetNode) {
      const gap = 16;
      const center = "50%";

      if (targetNode) {
        switch (position) {
          case "bottom":
            return {
              styles: { top: `calc(100% + ${gap}px)`, right: "auto", bottom: "auto", left: center },
              hiddenTransform: "translateX(-50%) translateY(8px)",
              showTransform: "translateX(-50%) translateY(0)",
            };
          case "left":
            return {
              styles: { top: center, right: "auto", bottom: "auto", left: `-${gap}px` },
              hiddenTransform: "translateX(calc(-100% - 8px)) translateY(-50%)",
              showTransform: "translateX(-100%) translateY(-50%)",
            };
          case "right":
            return {
              styles: { top: center, right: "auto", bottom: "auto", left: `calc(100% + ${gap}px)` },
              hiddenTransform: "translateX(8px) translateY(-50%)",
              showTransform: "translateX(0) translateY(-50%)",
            };
          default:
            return {
              styles: { top: `-${gap}px`, right: "auto", bottom: "auto", left: center },
              hiddenTransform: "translateX(-50%) translateY(calc(-100% - 8px))",
              showTransform: "translateX(-50%) translateY(-100%)",
            };
        }
      }

      switch (position) {
        case "bottom":
          return {
            styles: { top: "auto", right: "auto", bottom: `${gap}px`, left: center },
            hiddenTransform: "translateX(-50%) translateY(8px)",
            showTransform: "translateX(-50%) translateY(0)",
          };
        case "left":
          return {
            styles: { top: center, right: "auto", bottom: "auto", left: `${gap}px` },
            hiddenTransform: "translateX(-8px) translateY(-50%)",
            showTransform: "translateX(0) translateY(-50%)",
          };
        case "right":
          return {
            styles: { top: center, right: `${gap}px`, bottom: "auto", left: "auto" },
            hiddenTransform: "translateX(8px) translateY(-50%)",
            showTransform: "translateX(0) translateY(-50%)",
          };
        default:
          return {
            styles: { top: "130px", right: "auto", bottom: "auto", left: center },
            hiddenTransform: "translateX(-50%) translateY(-8px)",
            showTransform: "translateX(-50%) translateY(0)",
          };
      }
    }
  }

  if (!customElements.get("component-toast")) {
    customElements.define("component-toast", ComponentToast);
  }

  window.showToast = (options) => ComponentToast.create(options);

  document.addEventListener("toast:show", (event) => {
    window.showToast(event.detail || {});
  });

  /* --------------------------
   * Subscribe Form Web Component
   * ------------------------ */
  class SubscribeForm extends HTMLElement {
    static EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    static MESSAGES = {
      en: { error: "Subscription failed. Please try again later.", invalidEmail: "Please enter a valid email address.", success: "You're subscribed!" },
      de: { error: "Anmeldung fehlgeschlagen. Bitte versuchen Sie es später erneut.", invalidEmail: "Bitte geben Sie eine gültige E-Mail-Adresse ein.", success: "Du bist abonniert!" },
      fr: { error: "Échec de l'inscription. Veuillez réessayer plus tard.", invalidEmail: "Veuillez saisir une adresse e-mail valide.", success: "Inscription réussie !" },
      it: { error: "Iscrizione non riuscita. Riprova più tardi.", invalidEmail: "Inserisci un indirizzo email valido.", success: "Iscrizione riuscita!" },
      es: { error: "Error en la suscripción. Inténtalo de nuevo más tarde.", invalidEmail: "Introduce una dirección de correo electrónico válida.", success: "¡Suscripción realizada con éxito!" },
      nl: { error: "Inschrijving mislukt. Probeer het later opnieuw.", invalidEmail: "Voer een geldig e-mailadres in.", success: "Inschrijving geslaagd!" },
      pl: { error: "Subskrypcja nie powiodła się. Spróbuj ponownie później.", invalidEmail: "Wprowadź prawidłowy adres e-mail.", success: "Subskrypcja zakończona sukcesem!" },
      pt: { error: "Falha na subscrição. Tente novamente mais tarde.", invalidEmail: "Introduza um endereço de e-mail válido.", success: "Subscrição efetuada com sucesso!" },
      sv: { error: "Prenumerationen misslyckades. Försök igen senare.", invalidEmail: "Ange en giltig e-postadress.", success: "Prenumerationen lyckades!" },
      da: { error: "Tilmelding mislykkedes. Prøv igen senere.", invalidEmail: "Indtast en gyldig e-mailadresse.", success: "Tilmelding gennemført!" },
      fi: { error: "Tilaus epäonnistui. Yritä myöhemmin uudelleen.", invalidEmail: "Anna kelvollinen sähköpostiosoite.", success: "Tilaus onnistui!" },
      nb: { error: "Abonnementet mislyktes. Prøv igjen senere.", invalidEmail: "Skriv inn en gyldig e-postadresse.", success: "Abonnementet er fullført!" },
      cs: { error: "Přihlášení k odběru se nezdařilo. Zkuste to prosím později.", invalidEmail: "Zadejte platnou e-mailovou adresu.", success: "Přihlášení k odběru proběhlo úspěšně!" },
      el: { error: "Η εγγραφή απέτυχε. Δοκιμάστε ξανά αργότερα.", invalidEmail: "Εισαγάγετε μια έγκυρη διεύθυνση email.", success: "Η εγγραφή ολοκληρώθηκε με επιτυχία!" },
      hu: { error: "A feliratkozás sikertelen. Kérjük, próbálja meg később.", invalidEmail: "Adjon meg egy érvényes e-mail-címet.", success: "Sikeres feliratkozás!" },
      ro: { error: "Abonarea a eșuat. Încercați din nou mai târziu.", invalidEmail: "Introduceți o adresă de e-mail validă.", success: "Abonare reușită!" },
      sk: { error: "Prihlásenie na odber zlyhalo. Skúste to znova neskôr.", invalidEmail: "Zadajte platnú e-mailovú adresu.", success: "Prihlásenie na odber bolo úspešné!" },
      sl: { error: "Naročnina ni uspela. Poskusite znova pozneje.", invalidEmail: "Vnesite veljaven e-poštni naslov.", success: "Naročnina je uspela!" },
      hr: { error: "Pretplata nije uspjela. Pokušajte ponovno kasnije.", invalidEmail: "Unesite važeću adresu e-pošte.", success: "Pretplata je uspjela!" },
      bg: { error: "Абонаментът е неуспешен. Опитайте отново по-късно.", invalidEmail: "Въведете валиден имейл адрес.", success: "Абонаментът е успешен!" },
    };

    static getRouteLang() {
      const lang = (document.documentElement.lang || "en").toLowerCase();
      if (SubscribeForm.MESSAGES[lang]) return lang;
      const base = lang.split("-")[0] === "no" ? "nb" : lang.split("-")[0];
      return SubscribeForm.MESSAGES[base] ? base : "en";
    }

    static t(key) {
      const messages = SubscribeForm.MESSAGES[SubscribeForm.getRouteLang()] || SubscribeForm.MESSAGES.en;
      return messages[key] || SubscribeForm.MESSAGES.en[key];
    }

    constructor() {
      super();
      this.form = null;
      this.submitButton = null;
      this.isSubmitting = false;
      this.handleSubmit = this.submit.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector("form");
      this.submitButton = this.querySelector('button[type="submit"]');
      if (!this.form) return;
      this.form.addEventListener("submit", this.handleSubmit);
      this.setSubmitButtonLoading(false);
    }

    disconnectedCallback() {
      this.form?.removeEventListener("submit", this.handleSubmit);
    }

    async submit(ev) {
      ev.preventDefault();
      const errorMessage = SubscribeForm.t("error");
      if (this.isSubmitting) return;

      const email = this.form.querySelector('input[name="email"]')?.value.trim() || "";
      if (!SubscribeForm.EMAIL_PATTERN.test(email)) {
        this.showSubmitMessage(SubscribeForm.t("invalidEmail"), "error");
        return;
      }

      this.setSubmitButtonLoading(true);

      const eventSource = this.submitButton?.getAttribute("data-wd-subscribe-event");
      if (eventSource && typeof window.WDSendEvent === "function") {
        window.WDSendEvent(WD_EVENT_SUBSCRIBE_CLICK, { event_source: eventSource });
      }

      const formData = new FormData(this.form);
      const { action } = this.form;
      try {
        const response = await fetch(action, { method: "POST", body: formData, headers: { Accept: "application/json" } });

        if (!response.ok) {
          this.showSubmitMessage(errorMessage, "error");
          return;
        }

        const result = await response.json();
        if (result.code === 200 && result?.data) {
          const { prevState } = result.data;
          this.handleSubscribeSuccess(eventSource, prevState);
          return;
        }

        this.showSubmitMessage(result.message || errorMessage, "error");
      } catch (error) {
        console.error(error);
        this.showSubmitMessage(errorMessage, "error");
      } finally {
        this.setSubmitButtonLoading(false);
      }
    }

    // prevState: INVALID NOT_SUBSCRIBED PENDING SUBSCRIBED UNSUBSCRIBED
    /**
     * @description Handle subscribe success event and send event to GA4
     * prevState: INVALID NOT_SUBSCRIBED PENDING SUBSCRIBED UNSUBSCRIBED
     * eventLabel: 1.customer is not subscribed to email marketing.
     * eventLabel: 2.customer is not subscribed to email marketing but was previously subscribed.
     * eventLabel: 3.customer is subscribed to email marketing.
     * @param {string} eventSource
     * @param {string} prevState
     */
    handleSubscribeSuccess(eventSource, prevState) {
      if (eventSource && typeof window.WDSendEvent === "function") {
        let eventLabel = 3;
        if (prevState === "INVALID" || prevState === "NOT_SUBSCRIBED") eventLabel = 1;
        else if (prevState === "PENDING" || prevState === "UNSUBSCRIBED") eventLabel = 2;
        window.WDSendEvent(WD_EVENT_SUBSCRIBE_SUCCESS, { event_source: eventSource, event_label: eventLabel });
      }

      const successMessage = SubscribeForm.t("success");
      this.showSubmitMessage(successMessage, "success");
    }

    showSubmitMessage(message, type) {
      if (typeof window.showToast !== "function") return;
      window.showToast({ message, type, theme: "light", target: this });
    }

    setSubmitButtonLoading(isLoading) {
      this.isSubmitting = isLoading;
      this.toggleAttribute("submitting", isLoading);

      if (!this.submitButton) return;

      this.submitButton.disabled = isLoading;
      this.submitButton.classList.toggle("loading", isLoading);
      this.submitButton.setAttribute("aria-busy", isLoading.toString());
    }
  }

  if (!customElements.get("subscribe-form")) {
    customElements.define("subscribe-form", SubscribeForm);
  }
})();
